import React from "react";
import Drawer from "@material-ui/core/Drawer";
import { makeStyles } from "@material-ui/core/styles";
import IconButton from "@material-ui/core/IconButton";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Checkbox from "@material-ui/core/Checkbox";
import Collapse from "@material-ui/core/Collapse";
import CircularProgress from "@material-ui/core/CircularProgress";
import Close from "@material-ui/icons/Close";
import ExpandLess from "@material-ui/icons/ExpandLess";
import AddPhotoAlternate from "@material-ui/icons/AddPhotoAlternate";
import BrokenImage from "@material-ui/icons/BrokenImage";

const GRAPHQL_URL = (process.env.REACT_APP_API_URL || "http://localhost:5001") + "/graphql";

function authHeaders() {
  const token = localStorage.getItem("token");
  return {
    "Content-Type": "application/json",
    Authorization: token ? `Bearer ${token}` : "",
  };
}

function gql(query, variables) {
  return fetch(GRAPHQL_URL, {
    method: "POST",
    headers: authHeaders(),
    credentials: "include",
    body: JSON.stringify({ query, variables }),
  }).then((r) => r.json());
}

const GET_PRODUCT = `
  query GetProduct($id: String!) {
    getProduct(id: $id) {
      id productNumber barcode unit name
      category { id name }
      brand { id name }
      images costPrice sellingPrice
      onHand minInventoryLevel maxInventoryLevel
      position { id name }
      weight weightUnit
      description defaultNote
      forSale isActive
    }
  }
`;

const GET_DROPDOWNS = `
  query GetDropdowns {
    productCategories { id name }
    brands { id name }
    positions { id name }
  }
`;

const SAVE_MUTATION = `
  mutation CreateOrUpdateProduct($data: CreateProductDto!) {
    createOrUpdateProduct(data: $data)
  }
`;

const WEIGHT_UNITS = ["g", "kg", "mg", "oz", "lb"];

const useStyles = makeStyles({
  paper: {
    width: 720,
    display: "flex",
    flexDirection: "column",
    height: "100%",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "14px 24px",
    borderBottom: "1px solid #e0e0e0",
    flexShrink: 0,
  },
  title: { margin: 0, fontSize: "18px", fontWeight: 600 },
  body: {
    padding: "20px 24px",
    overflowY: "auto",
    flex: 1,
  },
  footer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "14px 24px",
    borderTop: "1px solid #e0e0e0",
    flexShrink: 0,
  },
  row: {
    display: "flex",
    gap: "12px",
    marginBottom: "14px",
    alignItems: "flex-start",
  },
  field: { flex: 1 },
  sectionHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "10px 0",
    borderBottom: "1px solid #e0e0e0",
    marginBottom: "14px",
    marginTop: "4px",
    cursor: "pointer",
    userSelect: "none",
  },
  sectionTitle: { fontWeight: 600, fontSize: "14px" },
  sectionSubtitle: { fontSize: "12px", color: "#888", marginTop: "2px" },
  loadingCenter: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flex: 1,
  },
});

export default function EditProductDialog({ productId, onClose, onSaved }) {
  const classes = useStyles();

  const [form, setForm] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState("");

  const [categories, setCategories] = React.useState([]);
  const [brands, setBrands] = React.useState([]);
  const [positions, setPositions] = React.useState([]);

  const [sections, setSections] = React.useState({
    price: true,
    onhand: true,
    description: true,
    location: true,
  });

  const toggleSection = (key) =>
    setSections((s) => ({ ...s, [key]: !s[key] }));

  const chevron = (open) => (
    <ExpandLess
      style={{
        transform: open ? "rotate(0deg)" : "rotate(180deg)",
        transition: "transform 0.2s",
        fontSize: "20px",
        color: "#555",
      }}
    />
  );

  const isNew = productId === "new";

  React.useEffect(() => {
    if (!productId) return;
    setLoading(true);
    setError("");
    setForm(null);

    const requests = isNew
      ? [Promise.resolve({ data: null }), gql(GET_DROPDOWNS, {})]
      : [gql(GET_PRODUCT, { id: productId }), gql(GET_DROPDOWNS, {})];

    Promise.all(requests)
      .then(([productRes, dropdownRes]) => {
        if (isNew) {
          setForm({
            id: null,
            productNumber: "", barcode: "", unit: "", name: "",
            categoryId: "", brandId: "", images: [],
            costPrice: 0, sellingPrice: 0,
            onHand: 0, minInventoryLevel: 0, maxInventoryLevel: 999999999,
            positionId: "", weight: 0, weightUnit: "g",
            description: "", defaultNote: "",
            forSale: true, isActive: true,
          });
        } else {
          if (productRes.errors) {
            setError(productRes.errors[0].message);
            return;
          }
          const p = productRes.data.getProduct;
          setForm({
            id: p.id,
            productNumber: p.productNumber || "",
            barcode: p.barcode || "",
            unit: p.unit || "",
            name: p.name || "",
            categoryId: p.category?.id || "",
            brandId: p.brand?.id || "",
            images: p.images || [],
            costPrice: p.costPrice ?? 0,
            sellingPrice: p.sellingPrice ?? 0,
            onHand: p.onHand ?? 0,
            minInventoryLevel: p.minInventoryLevel ?? 0,
            maxInventoryLevel: p.maxInventoryLevel ?? 999999999,
            positionId: p.position?.id || "",
            weight: p.weight ?? 0,
            weightUnit: p.weightUnit || "g",
            description: p.description || "",
            defaultNote: p.defaultNote || "",
            forSale: p.forSale !== false,
            isActive: p.isActive !== false,
          });
        }
        if (!dropdownRes.errors) {
          setCategories(dropdownRes.data.productCategories || []);
          setBrands(dropdownRes.data.brands || []);
          setPositions(dropdownRes.data.positions || []);
        }
      })
      .catch(() => setError("Không thể kết nối máy chủ"))
      .finally(() => setLoading(false));
  }, [productId]);

  const set = (field) => (e) =>
    setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSave = () => {
    if (!form.name.trim()) {
      setError("Product name is required");
      return;
    }
    setSaving(true);
    setError("");
    gql(SAVE_MUTATION, {
      data: {
        ...(form.id ? { id: form.id } : {}),
        productNumber: form.productNumber || null,
        barcode: form.barcode || null,
        unit: form.unit || null,
        name: form.name.trim(),
        categoryId: form.categoryId || null,
        brandId: form.brandId || null,
        images: form.images,
        costPrice: parseFloat(form.costPrice) || 0,
        sellingPrice: parseFloat(form.sellingPrice) || 0,
        onHand: parseFloat(form.onHand) || 0,
        minInventoryLevel: parseFloat(form.minInventoryLevel) || 0,
        maxInventoryLevel: parseFloat(form.maxInventoryLevel) || 999999999,
        positionId: form.positionId || null,
        weight: parseFloat(form.weight) || 0,
        weightUnit: form.weightUnit || "g",
        description: form.description || null,
        defaultNote: form.defaultNote || null,
        forSale: form.forSale,
        isActive: form.isActive,
      },
    })
      .then(({ data, errors }) => {
        if (errors) {
          setError(errors[0].message);
          return;
        }
        if (data.createOrUpdateProduct) {
          onSaved && onSaved(isNew ? "Product created successfully!" : "Product updated successfully!");
          onClose();
        }
      })
      .catch(() => setError("Không thể kết nối máy chủ"))
      .finally(() => setSaving(false));
  };

  return (
    <Drawer
      anchor="right"
      open={!!productId}
      onClose={onClose}
      classes={{ paper: classes.paper }}
    >
      {/* Header */}
      <div className={classes.header}>
        <h3 className={classes.title}>{isNew ? "Add product" : "Edit product"}</h3>
        <IconButton size="small" onClick={onClose}>
          <Close />
        </IconButton>
      </div>

      {/* Body */}
      {loading ? (
        <div className={classes.loadingCenter}>
          <CircularProgress />
        </div>
      ) : !form ? null : (
        <div className={classes.body}>
          {error && (
            <p style={{ color: "#f44336", marginBottom: "12px", fontSize: "13px" }}>
              {error}
            </p>
          )}

          {/* Top area: fields + image panel */}
          <div style={{ display: "flex", gap: "16px", marginBottom: "4px" }}>
            {/* Left: form fields */}
            <div style={{ flex: 1, minWidth: 0 }}>
              {/* Product number + Barcode */}
              <div className={classes.row}>
                <TextField
                  label="Product number"
                  value={form.productNumber}
                  onChange={set("productNumber")}
                  variant="outlined"
                  size="small"
                  className={classes.field}
                />
                <TextField
                  label="Barcode"
                  value={form.barcode}
                  onChange={set("barcode")}
                  variant="outlined"
                  size="small"
                  className={classes.field}
                />
              </div>

              {/* Product name */}
              <div className={classes.row}>
                <TextField
                  label="Product name"
                  value={form.name}
                  onChange={set("name")}
                  variant="outlined"
                  size="small"
                  className={classes.field}
                />
              </div>

              {/* Category + Brand */}
              <div className={classes.row}>
                <FormControl variant="outlined" size="small" className={classes.field}>
                  <InputLabel>Category</InputLabel>
                  <Select
                    value={form.categoryId}
                    onChange={set("categoryId")}
                    label="Category"
                  >
                    <MenuItem value=""><em>None</em></MenuItem>
                    {categories.map((c) => (
                      <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl variant="outlined" size="small" className={classes.field}>
                  <InputLabel>Brand</InputLabel>
                  <Select
                    value={form.brandId}
                    onChange={set("brandId")}
                    label="Brand"
                  >
                    <MenuItem value=""><em>None</em></MenuItem>
                    {brands.map((b) => (
                      <MenuItem key={b.id} value={b.id}>{b.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>
            </div>

            {/* Right: Image panel */}
            <div
              style={{
                width: "148px",
                flexShrink: 0,
                border: "1px dashed #ccc",
                borderRadius: "4px",
                padding: "10px 8px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <Button
                size="small"
                variant="outlined"
                startIcon={<AddPhotoAlternate style={{ fontSize: 16 }} />}
                style={{ fontSize: "12px", whiteSpace: "nowrap", padding: "4px 10px" }}
              >
                + Add image
              </Button>
              <span style={{ fontSize: "11px", color: "#999", textAlign: "center" }}>
                Up to 2 MB per image
              </span>
              {form.images.length > 0 ? (
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "4px",
                    width: "100%",
                  }}
                >
                  {form.images.slice(0, 4).map((img, i) => (
                    <div
                      key={i}
                      style={{
                        width: "100%",
                        paddingTop: "100%",
                        position: "relative",
                        border: "1px solid #eee",
                        borderRadius: "4px",
                        overflow: "hidden",
                        backgroundColor: "#f9f9f9",
                      }}
                    >
                      <img
                        src={img}
                        alt=""
                        style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                        onError={(e) => { e.target.style.display = "none"; }}
                      />
                    </div>
                  ))}
                </div>
              ) : (
                <BrokenImage style={{ fontSize: 40, color: "#ddd" }} />
              )}
            </div>
          </div>

          {/* ── Cost price, sold price ── */}
          <div
            className={classes.sectionHeader}
            onClick={() => toggleSection("price")}
          >
            <span className={classes.sectionTitle}>Cost price, sold price</span>
            {chevron(sections.price)}
          </div>
          <Collapse in={sections.price}>
            <div className={classes.row}>
              <TextField
                label="Cost price"
                type="number"
                value={form.costPrice}
                onChange={set("costPrice")}
                variant="outlined"
                size="small"
                className={classes.field}
                inputProps={{ min: 0 }}
              />
              <TextField
                label="Selling price"
                type="number"
                value={form.sellingPrice}
                onChange={set("sellingPrice")}
                variant="outlined"
                size="small"
                className={classes.field}
                inputProps={{ min: 0 }}
              />
            </div>
          </Collapse>

          {/* ── Onhand ── */}
          <div
            className={classes.sectionHeader}
            onClick={() => toggleSection("onhand")}
          >
            <div>
              <div className={classes.sectionTitle}>Onhand</div>
              <div className={classes.sectionSubtitle}>
                Manage on-hand quantity and inventory levels.
              </div>
            </div>
            {chevron(sections.onhand)}
          </div>
          <Collapse in={sections.onhand}>
            <div className={classes.row}>
              <TextField
                label="On hand"
                type="number"
                value={form.onHand}
                onChange={set("onHand")}
                variant="outlined"
                size="small"
                className={classes.field}
              />
              <TextField
                label="Min. inventory level"
                type="number"
                value={form.minInventoryLevel}
                onChange={set("minInventoryLevel")}
                variant="outlined"
                size="small"
                className={classes.field}
                inputProps={{ min: 0 }}
              />
              <TextField
                label="Max. inventory level"
                type="number"
                value={form.maxInventoryLevel}
                onChange={set("maxInventoryLevel")}
                variant="outlined"
                size="small"
                className={classes.field}
                inputProps={{ min: 0 }}
              />
            </div>
          </Collapse>

          {/* ── Description + Default notes ── */}
          <div
            className={classes.sectionHeader}
            onClick={() => toggleSection("description")}
          >
            <span className={classes.sectionTitle}>Description</span>
            {chevron(sections.description)}
          </div>
          <Collapse in={sections.description}>
            <div style={{ marginBottom: "14px" }}>
              <div style={{ fontSize: "13px", color: "#555", marginBottom: "6px" }}>
                Descr.
              </div>
              <textarea
                value={form.description}
                onChange={(e) =>
                  setForm((f) => ({ ...f, description: e.target.value }))
                }
                rows={4}
                placeholder="Product description..."
                style={{
                  width: "100%",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                  padding: "8px 10px",
                  fontSize: "14px",
                  fontFamily: "inherit",
                  resize: "vertical",
                  boxSizing: "border-box",
                  outline: "none",
                  color: "#333",
                }}
              />
            </div>
            <div
              style={{
                backgroundColor: "#f5f5f5",
                borderRadius: "4px",
                padding: "12px",
                marginBottom: "14px",
              }}
            >
              <div style={{ fontWeight: 600, fontSize: "13px", marginBottom: "8px", color: "#333" }}>
                Default notes (invoice, order)
              </div>
              <textarea
                value={form.defaultNote}
                onChange={(e) =>
                  setForm((f) => ({ ...f, defaultNote: e.target.value }))
                }
                rows={3}
                placeholder="Default notes..."
                style={{
                  width: "100%",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                  padding: "8px 10px",
                  fontSize: "14px",
                  fontFamily: "inherit",
                  resize: "vertical",
                  boxSizing: "border-box",
                  outline: "none",
                  color: "#333",
                  backgroundColor: "#fff",
                }}
              />
            </div>
          </Collapse>

          {/* ── Location, weight ── */}
          <div
            className={classes.sectionHeader}
            onClick={() => toggleSection("location")}
          >
            <div>
              <div className={classes.sectionTitle}>Location, weight</div>
              <div className={classes.sectionSubtitle}>
                Manage warehouse arrangement, sales position, or product weight.
              </div>
            </div>
            {chevron(sections.location)}
          </div>
          <Collapse in={sections.location}>
            <div className={classes.row}>
              <FormControl variant="outlined" size="small" className={classes.field}>
                <InputLabel>Position</InputLabel>
                <Select
                  value={form.positionId}
                  onChange={set("positionId")}
                  label="Position"
                >
                  <MenuItem value=""><em>None</em></MenuItem>
                  {positions.map((pos) => (
                    <MenuItem key={pos.id} value={pos.id}>{pos.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <div style={{ display: "flex", flex: 1 }}>
                <TextField
                  label="Weight"
                  type="number"
                  value={form.weight}
                  onChange={set("weight")}
                  variant="outlined"
                  size="small"
                  style={{ flex: 1 }}
                  inputProps={{ min: 0 }}
                  InputProps={{ style: { borderRadius: "4px 0 0 4px" } }}
                />
                <FormControl variant="outlined" size="small" style={{ minWidth: "72px" }}>
                  <Select
                    value={form.weightUnit}
                    onChange={set("weightUnit")}
                    style={{ borderRadius: "0 4px 4px 0" }}
                  >
                    {WEIGHT_UNITS.map((u) => (
                      <MenuItem key={u} value={u}>{u}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>
            </div>
          </Collapse>
        </div>
      )}

      {/* Footer */}
      <div className={classes.footer}>
        <FormControlLabel
          control={
            <Checkbox
              checked={form ? form.forSale : true}
              onChange={(e) => setForm((f) => ({ ...f, forSale: e.target.checked }))}
              color="primary"
              size="small"
            />
          }
          label={<span style={{ fontSize: "14px" }}>For sale</span>}
        />
        <div style={{ display: "flex", gap: "8px" }}>
          <Button variant="outlined" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={saving || loading || !form}
          >
            {saving ? <CircularProgress size={18} /> : "Save"}
          </Button>
        </div>
      </div>
    </Drawer>
  );
}
